import Phaser from 'phaser'
import {
  createTowerState,
  resolveHoneyVisual,
  type TowerV2State,
} from './towerModel'

import {
  HONEY_FISH_PATHS,
  HONEY_TOWER_DISPLAY_SCALE,
  HONEY_WATER_SURFACE_ALPHA,
} from './honeyRenderContract'

export const HONEY_V2_ASSETS = {
  composite: {
    key: 'tower-v2-honey-l0',
    path: 'sprites/v2/honey/tower-honey-b0s0-composite.png',
  },
  waterMask: {
    key: 'tower-v2-honey-water-mask',
    path: 'sprites/v2/honey/tower-honey-b0s0-water-mask.png',
  },
  waterSurface: {
    key: 'tower-v2-honey-water-surface',
    path: 'sprites/v2/honey/tower-honey-b0s0-water-surface.png',
  },
  fish: {
    key: 'tower-v2-honey-fish-swim',
    path: 'sprites/v2/honey/fish-honey-gourami-swim.png',
    frameWidth: 16,
    frameHeight: 16,
  },
} as const

interface FishState {
  sprite: Phaser.GameObjects.Sprite
  phase: number
  centerX: number
  centerY: number
  radiusX: number
  radiusY: number
  previousX: number
}

export class HoneyTowerV2View extends Phaser.GameObjects.Container {
  private readonly base: Phaser.GameObjects.Image
  private readonly fish: FishState[]
  private readonly waterSurface: Phaser.GameObjects.Image
  private readonly waterMaskSource: Phaser.GameObjects.Image
  private readonly waterMask: Phaser.Display.Masks.BitmapMask
  private readonly selectionOutline: Phaser.GameObjects.Rectangle
  private readonly fallbackBadge: Phaser.GameObjects.Text
  private upgradeState: TowerV2State = createTowerState()
  private fishAccumulator = 0
  private fishStep = 0
  private selectHandler: (() => void) | null = null

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, Math.round(x), Math.round(y))
    scene.add.existing(this)

    this.selectionOutline = scene.add.rectangle(0, -61, 124, 124, 0x000000, 0)
      .setStrokeStyle(2, 0xffd54f)
      .setVisible(false)
    this.add(this.selectionOutline)

    this.base = scene.add.image(0, 0, HONEY_V2_ASSETS.composite.key)
      .setOrigin(0.5, 122 / 128)
      .setInteractive({ useHandCursor: true })
    this.add(this.base)

    this.waterMaskSource = scene.add.image(this.x, this.y, HONEY_V2_ASSETS.waterMask.key)
      .setOrigin(0.5, 122 / 128)
      .setScale(HONEY_TOWER_DISPLAY_SCALE)
      .setVisible(false)
    this.waterMask = this.waterMaskSource.createBitmapMask()

    this.fish = HONEY_FISH_PATHS.map(path => this.createFish(
      path.centerX,
      path.centerY,
      path.radiusX,
      path.radiusY,
      path.phase,
    ))
    for (const fish of this.fish) fish.sprite.setMask(this.waterMask)

    this.waterSurface = scene.add.image(0, 0, HONEY_V2_ASSETS.waterSurface.key)
      .setOrigin(0.5, 122 / 128)
      .setAlpha(HONEY_WATER_SURFACE_ALPHA)
    this.add(this.waterSurface)

    this.fallbackBadge = scene.add.text(0, -126, '', {
      fontFamily: 'monospace',
      fontSize: '9px',
      color: '#ffffff',
      backgroundColor: '#ad1457',
      padding: { x: 3, y: 2 },
    }).setOrigin(0.5, 1).setVisible(false)
    this.add(this.fallbackBadge)

    this.base.on('pointerdown', () => {
      this.selectHandler?.()
    })

    this.setScale(HONEY_TOWER_DISPLAY_SCALE)
    this.setDepth(Math.round(y))
    this.setLevels(this.upgradeState)
  }

  private createFish(centerX: number, centerY: number, radiusX: number, radiusY: number, phase: number): FishState {
    const sprite = this.scene.add.sprite(centerX + radiusX, centerY, HONEY_V2_ASSETS.fish.key, phase % 4)
      .setOrigin(0.5)
    this.add(sprite)
    return { sprite, phase, centerX, centerY, radiusX, radiusY, previousX: sprite.x }
  }

  onSelect(handler: () => void): this {
    this.selectHandler = handler
    return this
  }

  getLevels(): TowerV2State {
    return { ...this.upgradeState }
  }

  setLevels(state: TowerV2State): void {
    this.upgradeState = { ...state }
    const visual = resolveHoneyVisual(state)
    this.base.setTexture(visual.compositeKey)
    this.fallbackBadge
      .setText(`B${state.bearLevel}/S${state.spaLevel} · L0 ART`)
      .setVisible(visual.usesFallbackArt)
    this.setAlpha(visual.usesFallbackArt ? 0.72 : 1)
  }

  setSelected(selected: boolean): void {
    this.selectionOutline.setVisible(selected)
  }

  canThrow(): boolean {
    return resolveHoneyVisual(this.upgradeState).canThrow
  }

  updateIdle(delta: number): void {
    this.fishAccumulator += delta
    while (this.fishAccumulator >= 140) {
      this.fishAccumulator -= 140
      this.fishStep++
      for (const fish of this.fish) {
        const step = (this.fishStep + fish.phase) % 24
        const angle = (step / 24) * Math.PI * 2
        const nextX = fish.centerX + Math.round(Math.cos(angle) * fish.radiusX)
        const nextY = fish.centerY + Math.round(Math.sin(angle) * fish.radiusY)
        fish.sprite
          .setPosition(nextX, nextY)
          .setFrame((this.fishStep + fish.phase) % 4)
          .setFlipX(nextX < fish.previousX)
        fish.previousX = nextX
      }
    }
  }

  override destroy(fromScene?: boolean): void {
    this.selectHandler = null
    for (const fish of this.fish) fish.sprite.clearMask()
    this.waterMask.destroy()
    this.waterMaskSource.destroy()
    super.destroy(fromScene)
  }
}
